import { motion } from 'framer-motion';
import Link from 'next/link';
import { useState } from 'react';

const VMCFAQ = () => {
  const [openIndices, setOpenIndices] = useState([]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const toggleAccordion = (index) => {
    if (openIndices.includes(index)) {
      setOpenIndices(openIndices.filter((i) => i !== index)); // Close accordion if it's already open
    } else {
      setOpenIndices([...openIndices, index]); // Open the clicked accordion
    }
  };

  const faqItems = [
    {
      question: 'Which Email Providers Will Display My VMC Logo?',
      answer:
        'Major providers like Gmail, Yahoo, Apple, and Microsoft Outlook support VMC logo display—Brandymail ensures your brand reaches recipients with enhanced trust.',
    },
    {
      question: 'What’s the Benefit of the Blue Checkmark with a VMC?',
      answer:
        'A VMC adds a verified blue checkmark in Gmail (currently the only provider offering this), boosting recipient trust—Brandymail helps you leverage this premium feature.',
    },
    {
      question: 'How Much Does a DigiCert VMC Cost for My Business?',
      answer:
        'VMC pricing varies, but Brandymail helps you choose between 12-month or cost-saving multi-year plans. Contact us for a tailored quote.',
    },
    {
      question: 'How Often Do I Need to Renew My VMC?',
      answer:
        'Your VMC needs revalidation every 397 days—Brandymail’s team simplifies the renewal process, ensuring compliance with multi-year plans.',
    },
    {
      question: 'What Are the Next Steps After Getting My VMC?',
      answer:
        'Host your logo and PEM file, then update your BIMI records—our Brandymail experts guide you through the process to boost email trust.',
    },
    {
      question: 'Do I Need to List All Domains When Purchasing a VMC?',
      answer:
        'List only your main domain; subdomains are covered. Brandymail helps you plan additional domains to optimize costs for your email strategy.',
    },
    {
      question: 'Will I Need Multiple VMCs for Different Logos?',
      answer:
        'Yes, each logo requires a VMC—Brandymail assists in budgeting and managing multiple VMCs to maximize your brand’s visibility.',
    },
    {
      question: 'What Are the Eligibility Requirements for a VMC?',
      answer:
        'You need a registered trademark and DMARC enforcement—Brandymail guides you through eligibility checks to ensure a smooth VMC application process.',
    },
  ];

  const handleScrollToPricing = (e) => {
    e.preventDefault();
    const pricingElement = document.getElementById('pricing');
    if (pricingElement) {
      const offsetTop = pricingElement.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div>
      <section className="section">
        <div className="container-default w-container">
          <div className="inner-container _700px---tablet center">
            <div className="inner-container _600px---mbl center">
              <motion.div
                className="mg-bottom-80px"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <div className="inner-container _745px center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        Frequently Asked Questions
                      </div>
                    </div>
                    <h2 className="display-2 color-neutral-100">VMC Questions Answered</h2>
                    <p className="color-neutral-300 mg-bottom-0 paragraph-medium">
                      Get answers to the most common questions about Verified Mark Certificates
                      (VMC) and how they can enhance your email brand presence with verified logos.
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="w-layout-grid grid-2-columns accordions-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {faqItems.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className={`accordion-item-wrapper ${openIndices.includes(index) ? 'open' : ''}`}
                    onClick={() => toggleAccordion(index)}
                    style={{
                      backgroundColor: openIndices.includes(index)
                        ? 'rgb(36, 123, 254)'
                        : 'rgb(40, 41, 43)',
                    }}
                  >
                    <div className="accordion-content-wrapper">
                      <div className="accordion-header">
                        <h3 className="text-400 bold color-neutral-100 mg-bottom-0">
                          {item.question}
                        </h3>
                      </div>
                      <div
                        className="acordion-body"
                        style={{
                          display: openIndices.includes(index) ? 'block' : 'none',
                          opacity: openIndices.includes(index) ? 1 : 0,
                          height: openIndices.includes(index) ? 'auto' : 0,
                          transition: 'opacity 0.3s, height 0.3s',
                        }}
                      >
                        <div className="accordion-spacer"></div>
                        <p className="color-neutral-100 mg-bottom-0">{item.answer}</p>
                      </div>
                    </div>
                    <div
                      className="btn-square-secondary small accordion-btn"
                      style={{
                        transform: openIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                        borderColor: openIndices.includes(index)
                          ? 'rgb(255, 255, 255)'
                          : 'rgb(36, 123, 254)',
                      }}
                    >
                      <div
                        className="accordion-item-line-icon horizontal"
                        style={{
                          backgroundColor: openIndices.includes(index)
                            ? 'rgb(255, 255, 255)'
                            : 'rgb(36, 123, 254)',
                          transform: openIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      ></div>
                      <div
                        className="accordion-item-line-icon vertical"
                        style={{
                          backgroundColor: openIndices.includes(index)
                            ? 'rgb(255, 255, 255)'
                            : 'rgb(36, 123, 254)',
                          transform: openIndices.includes(index)
                            ? 'rotate(270deg)'
                            : 'rotate(90deg)',
                        }}
                      ></div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                className="text-center mg-top-60px"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: 0.3,
                }}
                style={{ margin: '48px 0 0' }}
              >
                <Link
                  href="#pricing"
                  className="btn-primary w-button"
                  onClick={handleScrollToPricing}
                >
                  Get Started
                  <span className="line-rounded-icon link-icon-right"></span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <div className="divider _0px bg-neutral-700"></div>
    </div>
  );
};

export default VMCFAQ;
